import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import {
  AgentApprovalMode,
  AgentOutboundInput,
  AgentReplyWait,
  AgentReplyWaitInput,
  AgentTemplate,
  AgentToolPolicy,
  CreateAgentTemplateInput,
  UpdateAgentTemplateInput,
} from '@this+that/models';

import type { AgentOutboundWithScope } from '../components/AgentOutboundSettings/agentRecipientScope';

export const GET_AGENT_TEMPLATES = gql`
  query getAgentTemplates {
    getAgentTemplates {
      templateId
      name
      description
      systemPrompt
      toolSets
      mcpConnectionIds
      modelTier
      maxTurns
      icon
      isSystem
      isBoardAgent
      userId
      teamId
      outputSchema
      createdFromTemplateId
      approvalMode
      outbound {
        sender
        destinations {
          key
          platform
          workspaceId
          channelId
          kind
        }
        destinationCount
        mentionableUserIds
        allowBroadcastMentions
        emailRecipients {
          key
          address
          label
        }
        emailRecipientCount
        recipientScope
        slackPeopleScope
      }
      replyWait {
        enabled
        maxWaitHours
        remind
        rule
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_TEAM_AGENT_TEMPLATES = gql`
  query getTeamAgentTemplates {
    getTeamAgentTemplates {
      templateId
      name
      description
      systemPrompt
      toolSets
      mcpConnectionIds
      modelTier
      maxTurns
      icon
      isSystem
      isBoardAgent
      userId
      teamId
      outputSchema
      createdFromTemplateId
      approvalMode
      outbound {
        sender
        destinations {
          key
          platform
          workspaceId
          channelId
          kind
        }
        destinationCount
        mentionableUserIds
        allowBroadcastMentions
        emailRecipients {
          key
          address
          label
        }
        emailRecipientCount
        recipientScope
        slackPeopleScope
      }
      replyWait {
        enabled
        maxWaitHours
        remind
        rule
      }
      createdAt
      updatedAt
    }
  }
`;

const CREATE_AGENT_TEMPLATE = gql`
  mutation createAgentTemplate($input: CreateAgentTemplateInput!) {
    createAgentTemplate(input: $input) {
      templateId
      name
      description
      systemPrompt
      toolSets
      mcpConnectionIds
      modelTier
      maxTurns
      icon
      isSystem
      isBoardAgent
      userId
      teamId
      outputSchema
      createdFromTemplateId
      approvalMode
      outbound {
        sender
        destinations {
          key
          platform
          workspaceId
          channelId
          kind
        }
        destinationCount
        mentionableUserIds
        allowBroadcastMentions
        emailRecipients {
          key
          address
          label
        }
        emailRecipientCount
        recipientScope
        slackPeopleScope
      }
      replyWait {
        enabled
        maxWaitHours
        remind
        rule
      }
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_AGENT_TEMPLATE = gql`
  mutation updateAgentTemplate($templateId: String!, $input: UpdateAgentTemplateInput!) {
    updateAgentTemplate(templateId: $templateId, input: $input) {
      templateId
      name
      description
      systemPrompt
      toolSets
      mcpConnectionIds
      modelTier
      maxTurns
      icon
      isSystem
      isBoardAgent
      userId
      teamId
      outputSchema
      createdFromTemplateId
      approvalMode
      outbound {
        sender
        destinations {
          key
          platform
          workspaceId
          channelId
          kind
        }
        destinationCount
        mentionableUserIds
        allowBroadcastMentions
        emailRecipients {
          key
          address
          label
        }
        emailRecipientCount
        recipientScope
        slackPeopleScope
      }
      replyWait {
        enabled
        maxWaitHours
        remind
        rule
      }
      createdAt
      updatedAt
    }
  }
`;

const DELETE_AGENT_TEMPLATE = gql`
  mutation deleteAgentTemplate($templateId: String!) {
    deleteAgentTemplate(templateId: $templateId)
  }
`;

interface GetAgentTemplatesData {
  getAgentTemplates: AgentTemplate[];
}

interface GetTeamAgentTemplatesData {
  getTeamAgentTemplates: AgentTemplate[];
}

export function useGetAgentTemplates(skip = false) {
  return useQuery<GetAgentTemplatesData>(GET_AGENT_TEMPLATES, { skip });
}

export function useGetTeamAgentTemplates(skip = false) {
  return useQuery<GetTeamAgentTemplatesData>(GET_TEAM_AGENT_TEMPLATES, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    skip,
  });
}

export function useCreateAgentTemplate() {
  return useMutation<
    { createAgentTemplate: AgentTemplate },
    { input: CreateAgentTemplateInput }
  >(CREATE_AGENT_TEMPLATE, {
    refetchQueries: [
      { query: GET_AGENT_TEMPLATES },
      { query: GET_TEAM_AGENT_TEMPLATES },
    ],
  });
}

export function useUpdateAgentTemplate() {
  return useMutation<
    { updateAgentTemplate: AgentTemplate },
    { templateId: string; input: UpdateAgentTemplateInput }
  >(UPDATE_AGENT_TEMPLATE, {
    refetchQueries: [
      { query: GET_AGENT_TEMPLATES },
      { query: GET_TEAM_AGENT_TEMPLATES },
    ],
  });
}

export function useDeleteAgentTemplate() {
  return useMutation<unknown, { templateId: string }>(DELETE_AGENT_TEMPLATE, {
    refetchQueries: [
      { query: GET_AGENT_TEMPLATES },
      { query: GET_TEAM_AGENT_TEMPLATES },
    ],
  });
}

const GENERATE_AGENT_TEMPLATE_DRAFT = gql`
  mutation generateAgentTemplateDraft($input: GenerateAgentTemplateDraftInput!) {
    generateAgentTemplateDraft(input: $input) {
      name
      description
      systemPrompt
      toolSets
      modelTier
      maxTurns
      icon
      outputSchema
    }
  }
`;

export interface AgentTemplateDraftResult {
  name: string;
  description: string;
  systemPrompt: string;
  toolSets: string[];
  mcpConnectionIds?: string[];
  modelTier: string;
  maxTurns: number;
  icon?: string;
  outputSchema?: string;
}

interface GenerateAgentTemplateDraftData {
  generateAgentTemplateDraft: AgentTemplateDraftResult;
}

export function useGenerateAgentTemplateDraft() {
  const [generateDraftMutation, { loading }] = useMutation<
    GenerateAgentTemplateDraftData,
    { input: { prompt: string } }
  >(GENERATE_AGENT_TEMPLATE_DRAFT);

  const generateDraft = useCallback(
    async (prompt: string): Promise<AgentTemplateDraftResult | null> => {
      const { data } = await generateDraftMutation({
        variables: { input: { prompt } },
      });

      return data?.generateAgentTemplateDraft ?? null;
    },
    [generateDraftMutation],
  );

  return { generateDraft, loading };
}

/** Mirrors the GraphQL AgentTemplateVisibility enum — updateAgentDraft's way
 *  of choosing TEAM/PRIVATE without the caller ever setting teamId directly
 *  (the store derives it from the draft's source DoBox task group). */
export enum AgentTemplateVisibility {
  PRIVATE = 'PRIVATE',
  TEAM = 'TEAM',
}

export interface UpdateAgentDraftInput {
  name?: string;
  job?: string;
  /** Built-in sets only — MCP is never included here, it stays derived from
   *  toolPolicies (see AGENT_TEMPLATE_TOOL_OPTIONS/AgentToolSet.MCP). */
  toolSets?: string[];
  toolPolicies?: AgentToolPolicy[];
  visibility?: AgentTemplateVisibility;
  /** Owner-only; the setup conversation never sets it. */
  approvalMode?: AgentApprovalMode;
  /** Owner-only. Replaces the whole setting; null clears it so the agent cannot send. */
  outbound?: AgentOutboundInput | null;
  replyWait?: AgentReplyWaitInput | null;
}

export interface AgentDraftExampleFromItem {
  quote: string;
  author: string;
  channel: string;
}

export interface AgentDraftSourceRef {
  taskGroupId: string;
  entryId: string;
}

/** The agent-setup wizard's own view of an AgentTemplate — only the fields
 *  Steps 1-2 and Advanced settings' prefill need, never the full query
 *  below's selection (status/toolPolicies/exampleFromItem are DRAFT-only
 *  and absent from every other reader). */
export interface AgentSetupDraftTemplate {
  templateId: string;
  name: string;
  description: string;
  systemPrompt: string;
  toolSets: string[];
  mcpConnectionIds?: string[];
  modelTier: string;
  maxTurns: number;
  icon?: string;
  outputSchema?: string;
  teamId?: string;
  job?: string;
  toolPolicies?: AgentToolPolicy[];
  approvalRequiredForWrites?: boolean;
  approvalMode?: AgentApprovalMode;
  /** Null when never set. Lists come back empty for a non-owner; destinationCount says how many. */
  outbound?: AgentOutboundWithScope | null;
  replyWait?: AgentReplyWait | null;
  /** Server-derived: the agent only messages its owner, so no Slack destination is needed. */
  ownerNotificationOnly?: boolean;
  createdFromEntryRef?: AgentDraftSourceRef;
  exampleFromItem?: AgentDraftExampleFromItem;
  createdAt: number;
  updatedAt: number;
}

const AGENT_SETUP_DRAFT_TEMPLATE_FIELDS = `
  templateId
  name
  description
  systemPrompt
  toolSets
  mcpConnectionIds
  modelTier
  maxTurns
  icon
  outputSchema
  teamId
  job
  toolPolicies {
    connectionId
    allowedTools
    limits {
      tool
      param
      max
    }
  }
  approvalRequiredForWrites
  approvalMode
  outbound {
    sender
    destinations {
      key
      platform
      workspaceId
      channelId
      kind
    }
    destinationCount
    mentionableUserIds
    allowBroadcastMentions
    emailRecipients {
      key
      address
      label
    }
    emailRecipientCount
    recipientScope
    slackPeopleScope
  }
  replyWait {
    enabled
    maxWaitHours
    remind
    rule
  }
  ownerNotificationOnly
  createdFromEntryRef {
    taskGroupId
    entryId
  }
  exampleFromItem {
    quote
    author
    channel
  }
  createdAt
  updatedAt
`;

export enum AgentSetupConversationRole {
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
}

export enum AgentSetupConversationStatus {
  QUESTION = 'QUESTION',
  DRAFT_READY = 'DRAFT_READY',
  BLOCKED = 'BLOCKED',
}

export enum AgentSetupResultDelivery {
  DOBOX_COMMENT = 'DOBOX_COMMENT',
  REPLY_DRAFT = 'REPLY_DRAFT',
  NOTIFICATION = 'NOTIFICATION',
}

export interface AgentSetupConversationMessageInput {
  role: AgentSetupConversationRole;
  content: string;
}

export interface AgentSetupMissingCapability {
  description: string;
  integrationName?: string | null;
}

export interface AgentSetupMissingBrainReference {
  name: string;
  text: string;
}

export enum AgentSetupSuggestedDestinationKind {
  CHANNEL = 'CHANNEL',
  PERSON = 'PERSON',
}

export interface AgentSetupSuggestedDestination {
  kind: AgentSetupSuggestedDestinationKind;
  name: string;
}

export interface AgentSetupConversationTurn {
  status: AgentSetupConversationStatus;
  message?: string | null;
  suggestedReplies: string[];
  draft?: AgentSetupDraftTemplate | null;
  resultDelivery?: AgentSetupResultDelivery | null;
  missingCapabilities: AgentSetupMissingCapability[];
  /** Things to fix before the agent can work; non-empty only with DRAFT_READY. */
  caveats: string[];
  /** Brain pages or spaces the owner said do not exist yet, as of this turn (re-check before showing). */
  missingBrainReferences?: AgentSetupMissingBrainReference[];
  /** Slack channels or people the owner named as where results go, verbatim. */
  suggestedDestinations?: AgentSetupSuggestedDestination[];
}

const CONTINUE_AGENT_SETUP_CONVERSATION = gql`
  mutation continueAgentSetupConversation($taskGroupId: ID!, $entryId: ID!, $messages: [AgentSetupConversationMessageInput!]!, $templateId: String) {
    continueAgentSetupConversation(taskGroupId: $taskGroupId, entryId: $entryId, messages: $messages, templateId: $templateId) {
      status
      message
      suggestedReplies
      draft {
        ${AGENT_SETUP_DRAFT_TEMPLATE_FIELDS}
      }
      resultDelivery
      missingCapabilities {
        description
        integrationName
      }
      caveats
      missingBrainReferences {
        name
        text
      }
      suggestedDestinations {
        kind
        name
      }
    }
  }
`;

interface ContinueAgentSetupConversationData {
  continueAgentSetupConversation: AgentSetupConversationTurn;
}

interface ContinueAgentSetupConversationVariables {
  taskGroupId: string;
  entryId: string;
  messages: AgentSetupConversationMessageInput[];
  templateId?: string;
}

export function useContinueAgentSetupConversation() {
  return useMutation<
    ContinueAgentSetupConversationData,
    ContinueAgentSetupConversationVariables
  >(CONTINUE_AGENT_SETUP_CONVERSATION);
}

const UPDATE_AGENT_DRAFT = gql`
  mutation updateAgentDraft($templateId: String!, $input: UpdateAgentDraftInput!) {
    updateAgentDraft(templateId: $templateId, input: $input) {
      ${AGENT_SETUP_DRAFT_TEMPLATE_FIELDS}
    }
  }
`;

interface UpdateAgentDraftData {
  updateAgentDraft: AgentSetupDraftTemplate;
}

interface UpdateAgentDraftVariables {
  templateId: string;
  input: UpdateAgentDraftInput;
}

export function useUpdateAgentDraft() {
  return useMutation<UpdateAgentDraftData, UpdateAgentDraftVariables>(
    UPDATE_AGENT_DRAFT,
    {
      refetchQueries: [
        { query: GET_AGENT_TEMPLATES },
        { query: GET_TEAM_AGENT_TEMPLATES },
      ],
    },
  );
}

const ACTIVATE_AGENT_DRAFT = gql`
  mutation activateAgentDraft($templateId: String!) {
    activateAgentDraft(templateId: $templateId) {
      templateId
      name
      description
      systemPrompt
      toolSets
      mcpConnectionIds
      modelTier
      maxTurns
      icon
      isBoardAgent
      teamId
      status
      createdAt
      updatedAt
    }
  }
`;

interface ActivateAgentDraftData {
  activateAgentDraft: AgentTemplate;
}

export function useActivateAgentDraft() {
  return useMutation<ActivateAgentDraftData, { templateId: string }>(
    ACTIVATE_AGENT_DRAFT,
    {
      refetchQueries: [
        { query: GET_AGENT_TEMPLATES },
        { query: GET_TEAM_AGENT_TEMPLATES },
      ],
    },
  );
}

/** Regenerates the draft after the owner edits Step 1's job text — the
 *  backend re-runs the same generator continueAgentSetupConversation used, so the
 *  result can legitimately rename the agent and change its toolSets/
 *  toolPolicies. Signature assumed from the in-progress backend change
 *  (not yet confirmed against the live schema): `regenerateAgentDraft(
 *  templateId: String!, job: String!): AgentTemplate!`, returning the same
 *  AGENT_SETUP_DRAFT_TEMPLATE_FIELDS selection as continueAgentSetupConversation/
 *  updateAgentDraft. */
const REGENERATE_AGENT_DRAFT = gql`
  mutation regenerateAgentDraft($templateId: String!, $job: String!) {
    regenerateAgentDraft(templateId: $templateId, job: $job) {
      ${AGENT_SETUP_DRAFT_TEMPLATE_FIELDS}
    }
  }
`;

interface RegenerateAgentDraftData {
  regenerateAgentDraft: AgentSetupDraftTemplate;
}

interface RegenerateAgentDraftVariables {
  templateId: string;
  job: string;
}

export function useRegenerateAgentDraft() {
  return useMutation<RegenerateAgentDraftData, RegenerateAgentDraftVariables>(
    REGENERATE_AGENT_DRAFT,
  );
}

export type {
  AgentTemplate,
  CreateAgentTemplateInput,
  UpdateAgentTemplateInput,
};

const CHECK_AGENT_SETUP_BRAIN_REFERENCES = gql`
  query checkAgentSetupBrainReferences($names: [String!]!) {
    checkAgentSetupBrainReferences(names: $names) {
      name
      exists
    }
  }
`;

export interface AgentSetupBrainReferenceCheck {
  name: string;
  exists: boolean;
}

interface CheckAgentSetupBrainReferencesData {
  checkAgentSetupBrainReferences: AgentSetupBrainReferenceCheck[];
}

export function useCheckAgentSetupBrainReferences() {
  const client = useApolloClient();
  return useCallback(
    async (names: string[]): Promise<AgentSetupBrainReferenceCheck[]> => {
      const result = await client.query<CheckAgentSetupBrainReferencesData>({
        query: CHECK_AGENT_SETUP_BRAIN_REFERENCES,
        variables: { names },
        fetchPolicy: 'network-only',
      });
      return result.data?.checkAgentSetupBrainReferences ?? [];
    },
    [client],
  );
}
